import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { faArrowLeft, faTrash, faUser } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import languageStore from "@propai-web/lib/stores/language.store";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, useCanGoBack, useRouter } from "@tanstack/react-router";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { z } from "zod";

import { ConfirmationDialog, SolidCard } from "@utilifeed/components";
import { useDataApi } from "@core/apis";
import {
  addPropertyOrganizationUserMutation,
  getPropertyOrganizationUsersOptions,
  PropAIApi,
  removePropertyOrganizationUserMutation,
} from "@core/apis/PropAI";

const adminSearchSchema = z.object({
  from: z.string().optional(),
});

// Cell component for rendering the role chip
const AdminRoleCell = ({ isAdmin, t }: { isAdmin: boolean; t: any }) => {
  return (
    <Chip
      label={isAdmin ? t("_common:text_admin") : t("profile:text_read_only")}
      color={isAdmin ? "primary" : "default"}
      size="small"
    />
  );
};

export const Route = createFileRoute("/$organizationId/admin")({
  component: AdminPageComponent,
  validateSearch: adminSearchSchema,
  // Redirect if user is somehow able to get here without being admin
  beforeLoad: async ({ context }) => {
    const currentOrg = context.organization;

    if (!currentOrg?.is_admin) {
      throw redirect({
        to: "/app",
        replace: true,
      });
    }
  },
});

function AdminPageComponent() {
  const { t } = useTranslation(["propai_admin"]);
  const { organizationId } = Route.useParams();
  const selectedOrganization = Route.useRouteContext().organization;
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const dataApi = useDataApi() as PropAIApi;
  const queryClient = useQueryClient();
  const { currentLanguage } = languageStore();

  const [openDialog, setOpenDialog] = useState(false);
  const [email, setEmail] = useState("");

  // Fetch users
  const { data: users = [], isLoading } = useQuery(
    getPropertyOrganizationUsersOptions(dataApi, organizationId)
  );

  // Mutation for adding a user
  const addUserMutation = useMutation({
    ...addPropertyOrganizationUserMutation,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "property-organization", organizationId, "users"],
      });

      setOpenDialog(false);
      setEmail("");
    },
  });

  const [user, setUser] = useState<{ email: string; id: string } | null>(null);

  // Mutation for removing a user
  const removeUserMutation = useMutation({
    ...removePropertyOrganizationUserMutation,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "property-organization", organizationId, "users"],
      });
    },
  });

  const handleConfirmRemoveUser = () => {
    if (!user) return;
    removeUserMutation.mutate({
      propAiApi: dataApi,
      propertyOrganizationUid: organizationId,
      userUid: user.id,
    });
    setUser(null);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEmail("");
  };

  const handleSendInvite = () => {
    if (dataApi && email) {
      addUserMutation.mutate({
        propAiApi: dataApi,
        propertyOrganizationUid: organizationId,
        email,
        locale: currentLanguage,
      });
    }
  };

  const columns = [
    {
      header: t("email"),
      accessorKey: "email",
    },
    {
      header: t("role"),
      accessorKey: "is_admin",
      Cell: ({ cell }) => {
        const isAdmin = cell.getValue() as boolean;
        return <AdminRoleCell isAdmin={isAdmin} t={t} />;
      },
    },
    {
      header: t("_common:text_action"),
      accessorKey: "actions",
      enableSorting: false,
      enableColumnActions: false,
      Cell: ({ row }) => {
        const user = row.original;
        return (
          <ButtonGroup>
            <Button
              onClick={() => setUser({ email: user.email, id: user.uid })}
              variant="contained"
              color="error"
              data-testid="admin-remove-user-btn"
              disabled={user.is_admin}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </ButtonGroup>
        );
      },
    },
  ];

  const table = useMaterialReactTable({
    columns: columns,
    data: users,
    enableGlobalFilter: false,
    enableFilters: false,
    enableSorting: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enablePagination: false,
    state: {
      isLoading,
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: "calc(100vh - 220px)",
        minHeight: "300px",
      },
    },
  });

  return (
    <>
      <AppBar
        position="static"
        sx={{
          backgroundColor: "primary.dark",
          color: "white",
          height: "5.14rem",
          py: 1,
        }}
      >
        <Toolbar>
          <Grid container display="flex" alignItems="center" spacing={2}>
            <Grid item>
              {canGoBack ? (
                <IconButton
                  onClick={() => router.history.back()}
                  size="large"
                  sx={{
                    color: "white",
                    cursor: "pointer",
                  }}
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                </IconButton>
              ) : (
                <Link to="/app" style={{ textDecoration: "none", color: "inherit" }}>
                  <IconButton
                    size="large"
                    sx={{
                      color: "white",
                      cursor: "pointer",
                    }}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </IconButton>
                </Link>
              )}
            </Grid>
            <Grid item>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FontAwesomeIcon icon={faUser} size="lg" />
                <Typography variant="labelLarge" display="inline" pl={1} sx={{ color: "white" }}>
                  {selectedOrganization?.name} - {t("_common:text_admin")}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>

      <Grid container direction="row" spacing={2} p={2}>
        <Grid item xs={12}>
          <SolidCard title={t("user_permission_management")} testId="admin-members-table">
            <MaterialReactTable table={table} />
            <Button
              onClick={handleOpenDialog}
              size="small"
              variant="contained"
              color="primary"
              sx={{
                borderRadius: "16px",
                px: 2,
                py: 0.75,
                textTransform: "none",
                mt: 2,
              }}
            >
              {t("add_new_user")}
            </Button>
          </SolidCard>
        </Grid>
      </Grid>

      {/* Add User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: "1.125rem", fontWeight: 400 }}>
          {t("add_new_user")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
            {t("email")}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("enter_email_address")}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleSendInvite}
            size="small"
            variant="contained"
            color="primary"
            sx={{
              borderRadius: "16px",
              px: 3,
              py: 0.75,
              textTransform: "none",
            }}
            disabled={!email || addUserMutation.isPending}
          >
            {addUserMutation.isPending ? t("sending") : t("send_invite")}
          </Button>
          <Button
            onClick={handleCloseDialog}
            size="small"
            variant="outlined"
            sx={{ borderRadius: "16px", px: 3, py: 0.75, textTransform: "none" }}
          >
            {t("_action:action_cancel")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Modal */}
      <ConfirmationDialog
        id="remove-property-org-user"
        open={!!user}
        onClose={() => setUser(null)}
        title={t("propai_admin:title_remove_user")}
        contentText={t("propai_admin:text_remove_user", { email: user?.email })}
        onConfirm={handleConfirmRemoveUser}
      />
    </>
  );
}
